package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Finance_transaction
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Transactions financières. Les mutations propagent l'ajustement de solde au compte (et au
 * compte destinataire pour les transferts). La règle de l'effet sur les soldes vit dans
 * [applyEffectsNow], que le portefeuille d'une ligne de dépenses appelle aussi, dans la
 * même transaction SQL que sa sous-dépense.
 */
class FinanceTransactionRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Finance_transaction>> =
        db.financeTransactionQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeByAccount(accountId: String): Flow<List<Finance_transaction>> =
        db.financeTransactionQueries.selectByAccount(accountId).asFlow().mapToList(Dispatchers.Default)

    fun observeBetween(fromIso: String, toIso: String): Flow<List<Finance_transaction>> =
        db.financeTransactionQueries.selectBetween(fromIso, toIso).asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        date: String,
        accountId: String,
        toAccountId: String?,
        catId: String?,
        type: String,
        amount: Long,
        description: String?,
        fundId: String? = null,
        id: String = newId("tx"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.financeTransactionQueries.insert(
                id, date, accountId, toAccountId, catId, type, amount, description, fundId, now, now,
            )
            applyEffectsNow(db, type, accountId, toAccountId, amount, +1, now)
        }
        id
    }

    /**
     * Modifie une transaction existante. On annule d'abord les effets de l'ancienne
     * sur les soldes (compte source/destinataire), on écrit les nouveaux champs,
     * puis on applique les effets de la nouvelle version. Gère les changements de type,
     * de montant, de comptes (transferts) et de fund.
     */
    suspend fun update(
        old: Finance_transaction,
        date: String,
        accountId: String,
        toAccountId: String?,
        catId: String?,
        type: String,
        amount: Long,
        description: String?,
        fundId: String?,
    ) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            applyEffectsNow(db, old.type, old.account_id, old.to_account_id, old.amount, -1, now)
            db.financeTransactionQueries.update(
                date, accountId, toAccountId, catId, type, amount, description, fundId, now, old.id,
            )
            applyEffectsNow(db, type, accountId, toAccountId, amount, +1, now)
        }
    }

    /** Verdict du filtre cartésien : 'need', 'mastered' ou 'impulse'. */
    suspend fun setVerdict(id: String, verdict: String?) = withContext(Dispatchers.Default) {
        db.financeTransactionQueries.updateVerdict(verdict, nowMillis(), id)
    }

    suspend fun softDelete(tx: Finance_transaction) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.financeTransactionQueries.softDelete(now, now, tx.id)
            // Annule les effets de la transaction sur les soldes (compte).
            applyEffectsNow(db, tx.type, tx.account_id, tx.to_account_id, tx.amount, -1, now)
        }
    }

    companion object {
        /**
         * Applique (sign = +1) ou annule (sign = -1) l'effet d'une transaction sur les
         * soldes. Une règle, un seul endroit : create/update/softDelete et le portefeuille
         * d'une ligne de dépenses passent tous par ici, à l'intérieur d'une transaction SQL.
         */
        fun applyEffectsNow(
            db: TeddDatabase,
            type: String,
            accountId: String,
            toAccountId: String?,
            amount: Long,
            sign: Int,
            now: Long,
        ) {
            val s = sign.toLong()
            when (type) {
                "expense" -> db.financeAccountQueries.adjustBalance(s * -amount, now, accountId)
                "income" -> db.financeAccountQueries.adjustBalance(s * amount, now, accountId)
                "transfer" -> {
                    db.financeAccountQueries.adjustBalance(s * -amount, now, accountId)
                    if (toAccountId != null) db.financeAccountQueries.adjustBalance(s * amount, now, toAccountId)
                }
                // Un prêt : prêter et rembourser une dette sortent, emprunter et être remboursé entrent.
                LoanTx.LOAN_GIVEN, LoanTx.REPAYMENT_PAID -> db.financeAccountQueries.adjustBalance(s * -amount, now, accountId)
                LoanTx.LOAN_RECEIVED, LoanTx.REPAYMENT_RECEIVED -> db.financeAccountQueries.adjustBalance(s * amount, now, accountId)
            }
        }
    }
}
